import { FMSType, LeadInterface } from "@swivl-mono/types";
import { DateTime } from "luxon";

const FALLBACK_TZ = "America/New_York";

/**
 * FMS types whose legacy cart Day 0 is the LOCATION-LOCAL date-part of `leadCreatedDate`
 * (SSMCloud — ENG-2751, Cubby — ENG-3158, StorEdge — ENG-2849). Callers that resolve a
 * location timezone before invoking `legacyAbandonedCartDateValue` (the variable writer and
 * the legacy JIT matcher) gate the lookup on this set, so a new location-local FMS type is
 * added in exactly one place.
 *
 * StorEdge joined with ENG-2849: its cart-candidate population (open inquiries) was never
 * synced before that ticket — and since the reservation product call (2026-08-26) StorEdge
 * had zero candidates at all — so, like SSMCloud/Cubby at their launch, there are no
 * pre-existing variable values to preserve and the UTC-date-part legacy drift could be
 * retired instead of frozen.
 *
 * QuikStor, Tenant (Hummingbird) and Monument joined with ENG-3167 on the same basis: none of
 * them had ever been a candidate FMS, so there are no pre-existing variable values to preserve.
 */
export const LOCATION_LOCAL_CART_DAY_FMS_TYPES: ReadonlySet<string> = new Set([
  FMSType.SSMCloud,
  FMSType.Cubby,
  FMSType.StorEdge,
  FMSType.QuikStor,
  FMSType.Tenant,
  FMSType.Monument
]);

/**
 * Single source of truth for the `AbandonedCartDate` user-variable value (ENG-2937).
 *
 * Used by BOTH the server-side variable generation
 * (`LeadsService.processAbandonedCartDateForLocation`) and the legacy handler's JIT
 * lead reconstruction (`AbandonedCartSequenceUtils.pickCandidateLeadForDate`), so the
 * writer and the matcher cannot diverge.
 *
 * SiteLink takes the Eastern date-part: before ENG-2937 the lead feed's ET wall clock
 * was stored as if UTC, so the UTC date-part happened to equal the ET calendar day.
 * Now that `leadCreatedDate` is a correct instant, the ET date-part reproduces the
 * exact same variable values — the legacy engine's send days don't move. SiteLink was
 * deliberately NOT moved to the location-local set when StorEdge joined it (ENG-2849,
 * 2026-09-01): unlike SSMCloud/Cubby/StorEdge — which each joined with no pre-existing
 * variable values — SiteLink has live values and in-flight ladders, and flipping to
 * location-local would shift Day 0 by one day for evening-created leads at non-Eastern
 * locations mid-ladder (a step firing early or skipped). Revisit once live SiteLink
 * abandoned-cart locations are confirmed all-Eastern (then the flip is a value-level
 * no-op) or when SiteLink migrates to the new engine, whose `cartDay` is location-local
 * anyway. All other FMS types keep the UTC date-part their (unchanged) instants always
 * produced.
 *
 * SSMCloud (ENG-2751), Cubby (ENG-3158), and StorEdge (ENG-2849) take the LOCATION-LOCAL
 * date-part — the same reduction the new engine's `AbandonedCartRule.cartDay` performs — so
 * legacy and new engine derive the identical Day 0 (zero engine drift; possible because each
 * joined with no pre-existing variable values to preserve). Callers pass `locationTimezone` already
 * resolved through the same chain the new engine uses
 * (`resolveValidTz(resolveLocationTimezone(...))`); the ET fallback here only mirrors
 * `resolveValidTz`'s terminal default for a missing/invalid value, keeping degradation
 * symmetric between the engines.
 */
export function legacyAbandonedCartDateValue(
  lead: Pick<LeadInterface, "fmsType" | "leadCreatedDate">,
  locationTimezone?: string | null
): string | null {
  if (!lead.leadCreatedDate) {
    return null;
  }
  const leadCreatedDate = new Date(lead.leadCreatedDate);
  if (lead.fmsType === FMSType.SiteLink) {
    return DateTime.fromJSDate(leadCreatedDate, { zone: FALLBACK_TZ }).toISODate();
  }
  if (LOCATION_LOCAL_CART_DAY_FMS_TYPES.has(lead.fmsType)) {
    const localDate = DateTime.fromJSDate(leadCreatedDate, { zone: locationTimezone || FALLBACK_TZ });
    return (localDate.isValid ? localDate : DateTime.fromJSDate(leadCreatedDate, { zone: FALLBACK_TZ })).toISODate();
  }
  return leadCreatedDate.toISOString().slice(0, 10);
}
